import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Send,
  MessageCircle,
  Users,
  FileText,
  Star,
  Award,
  BookOpen,
  ChevronRight,
  Lightbulb,
  Shield,
  Target,
  Microscope,
  Globe,
  GraduationCap,
  ChartBar,
} from "lucide-react";

const MouseLight = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = React.useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, []);

  return (
    <div 
      className="absolute inset-0 overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      <div
        className="pointer-events-none absolute -inset-px transition-all duration-200 ease-out"
        style={{
          background: `radial-gradient(800px circle at ${mousePosition.x}px ${mousePosition.y}px, 
            rgba(139, 92, 246, 0.07),
            rgba(99, 102, 241, 0.07),
            transparent 40%)`
        }}
      />
    </div>
  );
};


const FeedbackForm = () => {
  const [formData, setFormData] = useState({
    subject: "",
    message: "",
    isAnonymous: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", content: "" });
  const [userInfo, setUserInfo] = useState(null);
  const [activeInfo, setActiveInfo] = useState(0);

  const guidelines = [
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "NEP 2020 Guidelines",
      content: "Regular alumni feedback helps shape curriculum development and industry alignment.",
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "UGC Framework",
      content: "Active alumni engagement ensures continuous improvement in academic processes.",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "NAAC Requirements",
      content: "Alumni feedback contributes to institutional quality metrics and accreditation.",
    },
  ];

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/alumni/me`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        const data = await response.json();
        setUserInfo(data);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();

    // Auto-rotate guidelines
    const interval = setInterval(() => {
      setActiveInfo((prev) => (prev + 1) % guidelines.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setMessage({
          type: "success",
          content: "Thank you! Your valuable feedback has been submitted.",
        });
        setFormData({
          subject: "",
          message: "",
          isAnonymous: false,
        });
      } else {
        throw new Error("Failed to submit feedback");
      }
    } catch (error) {
      setMessage({
        type: "error",
        content: "Unable to submit feedback. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };



  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 via-[#1a1437] to-gray-900 overflow-hidden">
        <MouseLight />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-28">
          <div className="text-center space-y-8">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm 
              border border-white/20 text-white">
              <MessageCircle className="h-4 w-4 mr-2 text-purple-300" />
              <span>Alumni Voice Portal</span>
            </div>

            <h1 className="text-6xl md:text-7xl font-bold tracking-tight">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-200 to-blue-200">
                Your Feedback
              </span>
              <span className="text-white ml-4">
                Shapes the Future
              </span>
            </h1>
            
            <p className="text-xl text-purple-100/80 max-w-3xl mx-auto font-light">
              Help us enhance the educational experience for future generations
            </p>
          </div>
        </div>

        {/* Wave bottom */}
        <div className="absolute bottom-0 inset-x-0">
          <svg 
            viewBox="0 0 1440 74" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-auto"
            preserveAspectRatio="none"
          >
            <path 
              d="M0 24C480 74 960 74 1440 24V74H0V24Z" 
              className="fill-gray-50"
            />
          </svg>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 pb-20">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Left Section - Updated with lighter design */}
          <div className="space-y-6">
            {/* Guidelines Card */}
            <div className="bg-white rounded-2xl shadow-lg p-8 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-blue-50/30" />
              
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <span className="px-4 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                    Educational Excellence Framework
                  </span>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-start space-x-4">
                    <div className="bg-purple-100 rounded-lg p-2 text-purple-600">
                      {guidelines[activeInfo].icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-gray-900">
                        {guidelines[activeInfo].title}
                      </h3>
                      <p className="text-gray-600">
                        {guidelines[activeInfo].content}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center space-x-2 mt-4">
                  {guidelines.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveInfo(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        activeInfo === index ? "bg-purple-600 w-6" : "bg-purple-200"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6 relative overflow-hidden">
                <h4 className="text-lg font-semibold mb-4 text-gray-900">Focus Areas</h4>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-3">
                    <div className="bg-blue-100 p-1.5 rounded-lg text-blue-600">
                      <Microscope className="w-4 h-4" />
                    </div>
                    <span className="text-gray-600">Research Initiatives</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="bg-blue-100 p-1.5 rounded-lg text-blue-600">
                      <Globe className="w-4 h-4" />
                    </div>
                    <span className="text-gray-600">Global Partnerships</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="bg-blue-100 p-1.5 rounded-lg text-blue-600">
                      <GraduationCap className="w-4 h-4" />
                    </div>
                    <span className="text-gray-600">Academic Excellence</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6 relative overflow-hidden">
                <h4 className="text-lg font-semibold mb-4 text-gray-900">Impact Areas</h4>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-3">
                    <div className="bg-purple-100 p-1.5 rounded-lg text-purple-600">
                      <BookOpen className="w-4 h-4" />
                    </div>
                    <span className="text-gray-600">Curriculum Design</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="bg-purple-100 p-1.5 rounded-lg text-purple-600">
                      <Users className="w-4 h-4" />
                    </div>
                    <span className="text-gray-600">Student Experience</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="bg-purple-100 p-1.5 rounded-lg text-purple-600">
                      <Target className="w-4 h-4" />
                    </div>
                    <span className="text-gray-600">Career Support</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Right Section - Feedback Form */}
          <div className="bg-white rounded-2xl shadow-lg p-8 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-blue-50/30" />

            <form onSubmit={handleSubmit} className="relative space-y-6">
              {userInfo && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      value={`${userInfo.firstName} ${userInfo.lastName}`}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={userInfo.user.email}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm"
                      readOnly
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 
                    focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                  placeholder="Brief subject of your feedback"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Your Feedback
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 
                    focus:ring-purple-500 focus:border-transparent transition-all min-h-[120px] text-sm"
                  placeholder="Share your thoughts, suggestions, or concerns..."
                  required
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="anonymous"
                  checked={formData.isAnonymous}
                  onChange={(e) => setFormData(prev => ({ ...prev, isAnonymous: e.target.checked }))}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <label htmlFor="anonymous" className="text-sm text-gray-700">
                  Submit anonymously
                </label>
              </div>

              {message.content && (
                <div className={`p-4 rounded-xl ${
                  message.type === "success"
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "bg-red-50 text-red-700 border border-red-200"
                }`}>
                  <p className="text-sm font-medium">{message.content}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 
                  text-white rounded-xl font-medium shadow-lg hover:shadow-xl 
                  transition-all flex items-center justify-center space-x-2 
                  disabled:opacity-70 text-sm hover:scale-[1.02] active:scale-[0.98]"
              >
                <Send className="w-4 h-4" />
                <span>{isLoading ? "Submitting..." : "Submit Feedback"}</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackForm;